import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE = `${process.env.REACT_APP_API_BASE_URL}/api/settings`;

// ─── Fee Categories ──────────────────────────────────────────────────────────

export const fetchFeeCategories = createAsyncThunk(
  'feesManagement/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/fee-categories/`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Fetch failed');
    }
  }
);

export const createFeeCategory = createAsyncThunk(
  'feesManagement/createCategory',
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE}/fee-categories/`, data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Create failed');
    }
  }
);

export const updateFeeCategory = createAsyncThunk(
  'feesManagement/updateCategory',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`${BASE}/fee-categories/${id}/`, data);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Update failed');
    }
  }
);

export const deleteFeeCategory = createAsyncThunk(
  'feesManagement/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE}/fee-categories/${id}/`);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Delete failed');
    }
  }
);

export const reorderFeeCategories = createAsyncThunk(
  'feesManagement/reorderCategories',
  async (orderedIds, { rejectWithValue }) => {
    try {
      await axios.post(`${BASE}/fee-categories/reorder/`, { ordered_ids: orderedIds });
      return orderedIds;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Reorder failed');
    }
  }
);

// ─── Default Rules ────────────────────────────────────────────────────────────

export const fetchDefaultRules = createAsyncThunk(
  'feesManagement/fetchDefaultRules',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/fee-rules/default/`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Fetch failed');
    }
  }
);

export const saveDefaultRules = createAsyncThunk(
  'feesManagement/saveDefaultRules',
  async (items, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE}/fee-rules/default/`, items);
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Save failed');
    }
  }
);

// ─── Class Rules ──────────────────────────────────────────────────────────────

export const fetchClassRules = createAsyncThunk(
  'feesManagement/fetchClassRules',
  async (classId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/fee-rules/by-class/${classId}/`);
      return { classId, rules: res.data };
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Fetch failed');
    }
  }
);

export const saveClassRules = createAsyncThunk(
  'feesManagement/saveClassRules',
  async ({ classId, items }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE}/fee-rules/by-class/${classId}/`, items);
      return { classId, result: res.data };
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Save failed');
    }
  }
);

// ─── Class Discounts ──────────────────────────────────────────────────────────

export const fetchClassDiscounts = createAsyncThunk(
  'feesManagement/fetchClassDiscounts',
  async (classId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/fee-discounts/by-class/${classId}/`);
      return { classId, discounts: res.data };
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Fetch failed');
    }
  }
);

export const saveClassDiscounts = createAsyncThunk(
  'feesManagement/saveClassDiscounts',
  async ({ classId, items }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE}/fee-discounts/by-class/${classId}/`, items);
      return { classId, result: res.data };
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Save failed');
    }
  }
);

// ─── Student Rules ────────────────────────────────────────────────────────────

export const fetchStudentFees = createAsyncThunk(
  'feesManagement/fetchStudentFees',
  async ({ studentId, schoolYearId }, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/fee-rules/by-student/`, {
        params: { student_id: studentId, school_year_id: schoolYearId },
      });
      return { studentId, schoolYearId, data: res.data };
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Fetch failed');
    }
  }
);

export const saveStudentFees = createAsyncThunk(
  'feesManagement/saveStudentFees',
  async ({ studentId, schoolYearId, items }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE}/fee-rules/by-student/`, {
        student_id: studentId,
        school_year_id: schoolYearId,
        items,
      });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Save failed');
    }
  }
);

// ─── Student Discounts ────────────────────────────────────────────────────────

export const fetchStudentDiscounts = createAsyncThunk(
  'feesManagement/fetchStudentDiscounts',
  async ({ studentId, schoolYearId }, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/fee-discounts/by-student/`, {
        params: { student_id: studentId, school_year_id: schoolYearId },
      });
      return { studentId, schoolYearId, discounts: res.data };
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Fetch failed');
    }
  }
);

export const saveStudentDiscounts = createAsyncThunk(
  'feesManagement/saveStudentDiscounts',
  async ({ studentId, schoolYearId, items }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE}/fee-discounts/by-student/`, {
        student_id: studentId,
        school_year_id: schoolYearId,
        items,
      });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Save failed');
    }
  }
);

// ─── Discounted Students List ──────────────────────────────────────────────────

export const fetchDiscountedStudents = createAsyncThunk(
  'feesManagement/fetchDiscountedStudents',
  async (params, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE}/fee-discounts/students/`, { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(e.response?.data || 'Fetch failed');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const feesManagementSlice = createSlice({
  name: 'feesManagement',
  initialState: {
    categories: [],
    defaultRules: [],
    classRules: {},       // { [classId]: [...] }
    classDiscounts: {},   // { [classId]: [...] }
    studentFees: null,
    studentDiscounts: null,
    discountedStudents: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearStudentFees(state) {
      state.studentFees = null;
      state.studentDiscounts = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const pending = (state) => { state.loading = true; state.error = null; };
    const rejected = (state, action) => { state.loading = false; state.error = action.payload; };

    builder
      // Categories
      .addCase(fetchFeeCategories.pending, pending)
      .addCase(fetchFeeCategories.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.categories = payload;
      })
      .addCase(fetchFeeCategories.rejected, rejected)

      .addCase(createFeeCategory.fulfilled, (state, { payload }) => {
        state.categories.push(payload);
      })
      .addCase(updateFeeCategory.fulfilled, (state, { payload }) => {
        const idx = state.categories.findIndex((c) => c.id === payload.id);
        if (idx !== -1) state.categories[idx] = payload;
      })
      .addCase(deleteFeeCategory.fulfilled, (state, { payload: id }) => {
        state.categories = state.categories.filter((c) => c.id !== id);
      })

      // Default rules
      .addCase(fetchDefaultRules.pending, pending)
      .addCase(fetchDefaultRules.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.defaultRules = payload;
      })
      .addCase(fetchDefaultRules.rejected, rejected)
      .addCase(saveDefaultRules.pending, (state) => { state.saving = true; })
      .addCase(saveDefaultRules.fulfilled, (state) => { state.saving = false; })
      .addCase(saveDefaultRules.rejected, (state, { payload }) => { state.saving = false; state.error = payload; })

      // Class rules
      .addCase(fetchClassRules.pending, pending)
      .addCase(fetchClassRules.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.classRules[payload.classId] = payload.rules;
      })
      .addCase(fetchClassRules.rejected, rejected)
      .addCase(saveClassRules.pending, (state) => { state.saving = true; })
      .addCase(saveClassRules.fulfilled, (state) => { state.saving = false; })
      .addCase(saveClassRules.rejected, (state, { payload }) => { state.saving = false; state.error = payload; })

      // Class discounts
      .addCase(fetchClassDiscounts.pending, pending)
      .addCase(fetchClassDiscounts.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.classDiscounts[payload.classId] = payload.discounts;
      })
      .addCase(fetchClassDiscounts.rejected, rejected)
      .addCase(saveClassDiscounts.pending, (state) => { state.saving = true; })
      .addCase(saveClassDiscounts.fulfilled, (state) => { state.saving = false; })
      .addCase(saveClassDiscounts.rejected, (state, { payload }) => { state.saving = false; state.error = payload; })

      // Student fees
      .addCase(fetchStudentFees.pending, pending)
      .addCase(fetchStudentFees.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.studentFees = payload.data;
      })
      .addCase(fetchStudentFees.rejected, rejected)
      .addCase(saveStudentFees.pending, (state) => { state.saving = true; })
      .addCase(saveStudentFees.fulfilled, (state) => { state.saving = false; })
      .addCase(saveStudentFees.rejected, (state, { payload }) => { state.saving = false; state.error = payload; })

      // Student discounts
      .addCase(fetchStudentDiscounts.pending, pending)
      .addCase(fetchStudentDiscounts.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.studentDiscounts = payload.discounts;
      })
      .addCase(fetchStudentDiscounts.rejected, rejected)
      .addCase(saveStudentDiscounts.pending, (state) => { state.saving = true; })
      .addCase(saveStudentDiscounts.fulfilled, (state) => { state.saving = false; })
      .addCase(saveStudentDiscounts.rejected, (state, { payload }) => { state.saving = false; state.error = payload; })

      // Discounted students list
      .addCase(fetchDiscountedStudents.pending, pending)
      .addCase(fetchDiscountedStudents.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.discountedStudents = payload;
      })
      .addCase(fetchDiscountedStudents.rejected, rejected);
  },
});

export const { clearStudentFees, clearError } = feesManagementSlice.actions;
export default feesManagementSlice.reducer;
